import React from 'react';
import { Formik } from 'formik';
import { normalizeTimes } from '@uplift/api';
import { CalendarDateService } from '@uplift/services';
import { TimeRange } from '@uplift/types';
import { Alert } from '../../../Alert';
import { AvailabilityFormSingleDateDialog } from '../../../molecules';
import { Box, Collapse, SnackbarContent } from '@mui/material';
import { useTimeoutResettableState } from '@uplift/hooks';


export type EditAvailabilityOverrideModalProps = {
  open: boolean;
  onClose: () => void;
  date: string;
  availability: TimeRange[];
  defaultAvailability?: TimeRange[];
  onAvailabilityChange?: (ranges: TimeRange[], date: string) => Promise<void>;
};


export const EditAvailabilityOverrideModal: React.FC<EditAvailabilityOverrideModalProps> = ({
  open,
  onClose,
  date,
  availability,
  defaultAvailability,
  onAvailabilityChange,
}) => {
  const [ errorMessage, setErrorMessage ] = useTimeoutResettableState<string>(5000);
  const dateObj = React.useMemo(() => CalendarDateService.date(date), [ date ]);

  return (
    <Formik
      enableReinitialize
      initialValues={{
        date: open ? date : undefined,
        availability: availability.map((a, i) => ({ ...a, id: i })) ?? [],
      }}
      onSubmit={async ({ availability }) => {
        const dayRanges = availability.map(r => ({ start: r.start, end: r.end }));
        const normalizedDayRanges = await normalizeTimes({ ranges: dayRanges, squash: true });

        await onAvailabilityChange?.(normalizedDayRanges.ranges, date);
        onClose();
      }}
    >
      <AvailabilityFormSingleDateDialog
        open={open}
        title={`Override availability for ${dateObj.format('MMM D')}`}
        handleClose={onClose}
        defaultAvailability={defaultAvailability}
        warningOnLessThenHour={true}
        onError={(err) => setErrorMessage(err)}
        prepend={(
          <Box position="relative" mx={2} mt={2}>
            <Alert severity="info">
              Previously booked sessions will not be canceled
            </Alert>
            <Box position="absolute" left="0" top="0" width="100%">
              <Collapse in={!!errorMessage}>
                <SnackbarContent message={errorMessage ?? '-'} style={{ height: '52px', boxShadow: 'none' }} />
              </Collapse>
            </Box>
          </Box>
        )}
        isAddingOverride
      />
    </Formik>
  );
};
